import { supabase } from '../lib/supabase';

/**
 * Tool Configuration Service
 * Manages Vapi tool configurations for Coach Assistants
 * Super Admin only
 */

/**
 * Get all tools (Super Admin) or active tools (all users)
 */
export const getTools = async (options = {}) => {
  try {
    const {
      search = '',
      isActive = null,
      sortBy = 'created_at',
      sortOrder = 'desc'
    } = options;

    let query = supabase
      .from('vapi_tools')
      .select('*');

    // Apply search filter
    if (search.trim()) {
      query = query.or(`name.ilike.%${search}%,description.ilike.%${search}%,function_name.ilike.%${search}%`);
    }

    // Apply active filter
    if (isActive !== null) {
      query = query.eq('is_active', isActive);
    }

    // Apply sorting
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching tools:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (err) {
    console.error('Error in getTools:', err);
    return { data: null, error: err.message };
  }
};

/**
 * Get a single tool by ID
 */
export const getToolById = async (toolId) => {
  try {
    const { data, error } = await supabase
      .from('vapi_tools')
      .select('*')
      .eq('id', toolId)
      .single();

    if (error) {
      console.error('Error fetching tool:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (err) {
    console.error('Error in getToolById:', err);
    return { data: null, error: err.message };
  }
};

/**
 * Create a new tool
 */
export const createTool = async (toolData) => {
  try {
    const {
      name,
      description,
      type = 'function',
      function_name,
      function_description,
      function_parameters = {},
      server_url,
      server_timeout_seconds = 20,
      server_headers = {},
      messages = [],
      is_active = true
    } = toolData;

    const { data: tool, error } = await supabase
      .from('vapi_tools')
      .insert({
        name,
        description,
        type,
        function_name,
        function_description,
        function_parameters,
        server_url,
        server_timeout_seconds,
        server_headers,
        messages,
        is_active,
        created_by: (await supabase.auth.getUser()).data.user?.id
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating tool:', error);
      return { data: null, error: error.message };
    }

    return { data: tool, error: null };
  } catch (err) {
    console.error('Error in createTool:', err);
    return { data: null, error: err.message };
  }
};

/**
 * Update an existing tool
 */
export const updateTool = async (toolId, toolData) => {
  try {
    const updateData = {};
    
    // Only include fields that are provided
    if (toolData.name !== undefined) updateData.name = toolData.name;
    if (toolData.description !== undefined) updateData.description = toolData.description;
    if (toolData.type !== undefined) updateData.type = toolData.type;
    if (toolData.function_name !== undefined) updateData.function_name = toolData.function_name;
    if (toolData.function_description !== undefined) updateData.function_description = toolData.function_description;
    if (toolData.function_parameters !== undefined) updateData.function_parameters = toolData.function_parameters;
    if (toolData.server_url !== undefined) updateData.server_url = toolData.server_url;
    if (toolData.server_timeout_seconds !== undefined) updateData.server_timeout_seconds = toolData.server_timeout_seconds;
    if (toolData.server_headers !== undefined) updateData.server_headers = toolData.server_headers;
    if (toolData.messages !== undefined) updateData.messages = toolData.messages;
    if (toolData.is_active !== undefined) updateData.is_active = toolData.is_active;

    const { data: tool, error } = await supabase
      .from('vapi_tools')
      .update(updateData)
      .eq('id', toolId)
      .select()
      .single();

    if (error) {
      console.error('Error updating tool:', error);
      return { data: null, error: error.message };
    }

    return { data: tool, error: null };
  } catch (err) {
    console.error('Error in updateTool:', err);
    return { data: null, error: err.message };
  }
};

/**
 * Delete a tool (will fail if tool is in use by assistants)
 */
export const deleteTool = async (toolId) => {
  try {
    // Check if tool is in use
    const { data: associations, error: assocError } = await supabase
      .from('vapi_assistant_tools')
      .select('assistant_id')
      .eq('tool_id', toolId);

    if (assocError) {
      console.error('Error checking tool associations:', assocError);
      return { success: false, error: assocError.message };
    }

    if (associations && associations.length > 0) {
      return {
        success: false,
        error: `Cannot delete tool. It is currently used by ${associations.length} assistant(s). Remove it from all assistants first.`
      };
    }

    const { error } = await supabase
      .from('vapi_tools')
      .delete()
      .eq('id', toolId);

    if (error) {
      console.error('Error deleting tool:', error);
      return { success: false, error: error.message };
    }

    return { success: true, error: null };
  } catch (err) {
    console.error('Error in deleteTool:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Toggle tool active status
 */
export const toggleToolStatus = async (toolId, isActive) => {
  try {
    const { data, error } = await supabase
      .from('vapi_tools')
      .update({ is_active: isActive })
      .eq('id', toolId)
      .select()
      .single();

    if (error) {
      console.error('Error toggling tool status:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (err) {
    console.error('Error in toggleToolStatus:', err);
    return { data: null, error: err.message };
  }
};

/**
 * Build Vapi tool configuration from database record
 * Converts DB format to Vapi SDK format
 */
export const buildVapiToolConfig = (toolRecord) => {
  return {
    type: toolRecord.type || 'function',
    function: {
      name: toolRecord.function_name,
      description: toolRecord.function_description,
      parameters: toolRecord.function_parameters
    },
    messages: toolRecord.messages || [],
    server: {
      url: toolRecord.server_url,
      timeoutSeconds: toolRecord.server_timeout_seconds || 20,
      ...(toolRecord.server_headers && Object.keys(toolRecord.server_headers).length > 0 
        ? { headers: toolRecord.server_headers } 
        : {})
    }
  };
};

export default {
  getTools,
  getToolById,
  createTool,
  updateTool,
  deleteTool,
  toggleToolStatus,
  buildVapiToolConfig
};